import type { HTMLAttributes, ReactElement } from 'react'
import type { List } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import cc from 'classcat'

import type { BlockTemplateType } from './editorSidebarBlockTemplates'
import { ContentCreationManager } from './ContentCreationManager/ContentCreationManager'
import { Tooltip } from './Tooltip'
import { VideoTutorialProvider } from './videoTutorials/VideoTutorialContext'
import { activateEditorSidebarModule, setMboMenuMode } from '../store/actions'
import { useTranslate } from '../utils/translate'
import MBOMenu from './MBOMenu'
import PageManager from '../components/pageManager/PageManager'
import SaveFeedbackIndicator from './SaveFeedbackIndicator'
import ThemeManager from './themeManager/ThemeManager'
import VideoTutorialModal from './videoTutorials/VideoTutorialModal'
import useEventHandler from '../utils/hooks/useEventHandler'

export function createToolDragAndDropPreviewNode(blockTemplate: BlockTemplateType): HTMLDivElement {
  const node = document.createElement('div')
  node.className = 'ep-content-element-drag-preview'
  node.innerHTML = blockTemplate.icon
  return node
}

type BlockTemplateProps = {
  icon: string
  isDragActive: boolean
  isBlockDisabled?: boolean
  tReady?: boolean
} & HTMLAttributes<HTMLDivElement>

export function BlockTemplate({
  icon,
  isDragActive: _isDragActive,
  tReady: _tReady,
  ...others
}: Readonly<BlockTemplateProps>): ReactElement {
  /* SVG code needs to be saved here editorSidebarBlockTemplates.tsx */
  return <div {...others} dangerouslySetInnerHTML={{ __html: icon }} />
}

export const editorSidebarModuleNames = ['theme-manager', 'content-creation', 'page-manager', 'none'] as const
export type SidebarModuleName = ValuesOf<typeof editorSidebarModuleNames>

type EditorSidebarProps = {
  location: any
  navigation: any
  legalPages: List<ImmutableMap>
  footerPages: List<ImmutableMap>
  activeModule: SidebarModuleName
  mboMenuMode?: boolean
  contentCreationDisabled?: boolean
  editorDisabled?: boolean
}

export default function EditorSidebar({
  location,
  navigation,
  legalPages,
  footerPages,
  activeModule,
  contentCreationDisabled,
  editorDisabled,
  mboMenuMode,
}: Readonly<EditorSidebarProps>): ReactElement {
  const dispatch = useDispatch<GlobalDispatch>()
  const isBeyond = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const activePageBlocks = useSelector<State, any>((state) =>
    state.getIn(['pages', state.getIn(['view', 'activePageSlug']), 'content', 'blocks']),
  )

  const t = useTranslate('interface', 'components.editorSidebarComponent')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(followDeepLinks, [])
  useEventHandler('hashchange', followDeepLinks)
  function followDeepLinks() {
    switch (window.location.hash) {
      // TODO:
      // Remove this case when all links to #themeSelection are replaced with deep links to /editor/themes.
      // See https://epages.atlassian.net/browse/UNITY-7968
      // Toggle theme manager in case of deep linking to it.
      case '#themeSelection':
        toggleModule('theme-manager')
        break
      case '#imageContentElements':
      case '#textContentElements':
        if (activeModule !== 'content-creation') toggleModule('content-creation')
    }
  }

  function toggleModule(name: SidebarModuleName) {
    dispatch(activateEditorSidebarModule(name === activeModule ? 'none' : name))
  }

  const toggleArrow = <span className="ep-sidebar-module-header-arrow" />

  const handleBackButton = () => dispatch(setMboMenuMode(true))

  const contentCreationHeader = (
    <div
      onClick={() => contentCreationDisabled || toggleModule('content-creation')}
      className={cc([
        'ep-sidebar-module-header',
        {
          active: activeModule === 'content-creation',
          disabled: contentCreationDisabled,
        },
      ])}
    >
      {t('contentElementsSection.title')}
      {toggleArrow}
    </div>
  )

  return (
    <VideoTutorialProvider>
      <div className="ep-menu-sidebar-container">
        <div
          className={cc([
            'ep-menu-sidebar-slider',
            {
              'shows-mbo-menu': mboMenuMode,
            },
          ])}
        >
          <nav className="MainMenuBarWrapper">
            <MBOMenu />
          </nav>
          <div className="ep-editor-sidebar">
            {!isBeyond && (
              <div className="ep-editor-sidebar-back">
                <button className="ep-editor-sidebar-back-button" onClick={handleBackButton}>
                  <span className="ep-editor-sidebar-back-button-arrow" />
                  {t('mainMenuLink')}
                </button>
              </div>
            )}

            {/* theme manager */}
            <div
              onClick={() => editorDisabled || toggleModule('theme-manager')}
              className={cc(['ep-sidebar-module-header', { active: activeModule === 'theme-manager' }])}
            >
              {t('themeManagerSection.title')}
              {toggleArrow}
            </div>
            <ThemeManager t={(key: string) => t(`themeManagerSection.${key}`)} />

            {/* content creation */}
            {contentCreationDisabled ? (
              <Tooltip
                trigger={contentCreationHeader}
                content={t('contentElementsSection.disabledState.explanation')}
                side="bottom"
                hideIcon
              />
            ) : (
              <>
                {contentCreationHeader}
                <ContentCreationManager
                  isBeyond={isBeyond}
                  resetCategory={activeModule !== 'content-creation'}
                  t={t}
                  activePageBlocks={activePageBlocks}
                />
              </>
            )}

            {/* page manager */}
            <div
              onClick={() => editorDisabled || toggleModule('page-manager')}
              className={cc([
                'ep-sidebar-module-header',
                {
                  active: activeModule === 'page-manager',
                },
              ])}
            >
              {t('pageManagerSection.title')}
              {toggleArrow}
            </div>
            <PageManager
              location={location}
              navigation={navigation}
              disabled={editorDisabled}
              legalPages={legalPages}
              footerPages={footerPages}
              className="ep-sidebar-module-content ep-sidebar-module-page-manager"
            />
          </div>
          <SaveFeedbackIndicator />
        </div>
      </div>
      <VideoTutorialModal t={t} />
    </VideoTutorialProvider>
  )
}
